// src/components/CustomLearningModulesManager.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Stack, Stepper, Step, StepLabel, Card, CardContent,
  CardMedia, CardActions, Chip, Divider, FormControl, InputLabel, Select,
  MenuItem, RadioGroup, FormControlLabel, Radio, CircularProgress, Alert,
  Tooltip, Grid, Switch, Accordion, AccordionSummary, AccordionDetails, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ImageIcon from '@mui/icons-material/Image';
import QuizIcon from '@mui/icons-material/Quiz';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import API_ENDPOINTS from '../config/apiConfig';
import FixedSnackbar from './FixedSnackbar';
import { useTranslation } from '../i18n/i18n';

const WIZARD_STEPS_BASIC = 0;
const WIZARD_STEPS_MEDIA = 1;
const WIZARD_STEPS_QUIZ = 2;

function CustomLearningModulesManager() {
  const { t } = useTranslation();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [editingVideoid, setEditingVideoid] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedSection, setExpandedSection] = useState(false);
  const [searchBuiltIn, setSearchBuiltIn] = useState('');
  const [searchCustom, setSearchCustom] = useState('');
  const [sortBuiltIn, setSortBuiltIn] = useState('default-first');
  const [sortCustom, setSortCustom] = useState('default-first');

  // Wizard form state
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPassingScore, setFormPassingScore] = useState(70);
  const [formVideoType, setFormVideoType] = useState('AzureStorage');
  const [formVideoURL, setFormVideoURL] = useState('');
  const [formThumbnailURL, setFormThumbnailURL] = useState('');
  const [formQuestions, setFormQuestions] = useState([]);

  // Upload state
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [thumbnailSasUrls, setThumbnailSasUrls] = useState({}); // { [videoid]: sas_url }

  const fetchThumbnailSas = async (videoid) => {
    try {
      const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/learning-modules/client-sas/thumbnail-sas/${encodeURIComponent(videoid)}`, { credentials: 'include' });
      if (!res.ok) return null;
      const json = await res.json();
      return json?.sas_url || null;
    } catch {
      return null;
    }
  };

  const fetchModules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.LEARNING_MODULES_ALL, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      const mods = Array.isArray(data) ? data : [];
      setModules(mods);

      // Resolve thumbnail SAS URLs for all modules with thumbnails
      const sasMap = {};
      await Promise.all(mods.filter(m => m.thumbnailURL && m.videoid).map(async (m) => {
        const url = await fetchThumbnailSas(m.videoid);
        if (url) sasMap[m.videoid] = url;
      }));
      if (Object.keys(sasMap).length > 0) {
        setThumbnailSasUrls(prev => ({ ...prev, ...sasMap }));
      }
    } catch {
      setModules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const resetWizard = () => {
    setWizardStep(0);
    setEditingVideoid(null);
    setFormTitle('');
    setFormDescription('');
    setFormPassingScore(70);
    setFormVideoType('AzureStorage');
    setFormVideoURL('');
    setFormThumbnailURL('');
    setFormQuestions([]);
    setVideoFile(null);
    setThumbnailFile(null);
    setUploadProgress('');
  };

  const openCreateWizard = () => {
    resetWizard();
    setWizardOpen(true);
  };

  const openEditWizard = (module) => {
    setEditingVideoid(module.videoid);
    setFormTitle(module.title || '');
    setFormDescription(module.description || '');
    setFormPassingScore(module.passingScore || 70);
    setFormVideoType(module.videoType || 'AzureStorage');
    setFormVideoURL(module.videoURL || '');
    setFormThumbnailURL(module.thumbnailURL || '');
    setFormQuestions(JSON.parse(JSON.stringify(module.questions || [])));
    setWizardStep(0);
    setWizardOpen(true);
  };

  const handleToggleDefault = async (videoid, currentValue) => {
    try {
      const res = await fetch(API_ENDPOINTS.LEARNING_MODULE_SET_DEFAULT(videoid), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_default: !currentValue }),
      });
      if (res.ok) {
        setModules(prev => prev.map(m =>
          m.videoid === videoid ? { ...m, is_default: !currentValue } : m
        ));
      } else {
        setSnackbar({ open: true, message: t('modules_update_error'), severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: t('modules_update_error'), severity: 'error' });
    }
  };

  const uploadFileToSAS = async (file, mediaType) => {
    // Get SAS URL from backend
    const sasRes = await fetch(`${API_ENDPOINTS.BASEURL}/api/learning-modules/client-sas/upload/${mediaType}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name })
    });
    if (!sasRes.ok) throw new Error(`Failed to get upload SAS for ${mediaType}`);
    const { sas_url, blob_url } = await sasRes.json();

    // Upload directly to Azure Blob via SAS
    const uploadRes = await fetch(sas_url, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': file.type || 'application/octet-stream'
      },
      body: file
    });
    if (!uploadRes.ok) throw new Error(`Upload failed for ${mediaType}`);

    return blob_url;
  };

  const handleWizardNext = async () => {
    if (wizardStep === WIZARD_STEPS_BASIC) {
      if (!formTitle.trim()) {
        setSnackbar({ open: true, message: t('clm_title_required'), severity: 'error' });
        return;
      }
      setWizardStep(WIZARD_STEPS_MEDIA);
    } else if (wizardStep === WIZARD_STEPS_MEDIA) {
      // Upload files if selected
      if (videoFile || thumbnailFile) {
        setUploading(true);
        try {
          if (videoFile) {
            setUploadProgress(t('clm_uploading_video'));
            const videoUrl = await uploadFileToSAS(videoFile, 'video');
            setFormVideoURL(videoUrl);
          }
          if (thumbnailFile) {
            setUploadProgress(t('clm_uploading_thumbnail'));
            const thumbUrl = await uploadFileToSAS(thumbnailFile, 'thumbnail');
            setFormThumbnailURL(thumbUrl);
          }
          setUploadProgress('');
        } catch (err) {
          setSnackbar({ open: true, message: err.message || t('clm_upload_failed'), severity: 'error' });
          setUploading(false);
          setUploadProgress('');
          return;
        }
        setUploading(false);
      }
      setWizardStep(WIZARD_STEPS_QUIZ);
    }
  };

  const handleWizardBack = () => {
    if (wizardStep > 0) setWizardStep(wizardStep - 1);
  };

  const handleWizardSave = async () => {
    if (formQuestions.length === 0) {
      setSnackbar({ open: true, message: t('clm_questions_required'), severity: 'error' });
      return;
    }
    // Validate each question has correctAnswer
    for (let i = 0; i < formQuestions.length; i++) {
      const q = formQuestions[i];
      if (!q.question?.trim()) {
        setSnackbar({ open: true, message: t('clm_question_text_required') + ` (#${i + 1})`, severity: 'error' });
        return;
      }
      if (!q.correctAnswer?.trim()) {
        setSnackbar({ open: true, message: t('clm_correct_answer_required') + ` (#${i + 1})`, severity: 'error' });
        return;
      }
      if (!q.options || q.options.length < 2) {
        setSnackbar({ open: true, message: t('clm_min_options') + ` (#${i + 1})`, severity: 'error' });
        return;
      }
    }

    const payload = {
      title: formTitle.trim(),
      description: formDescription.trim(),
      passingScore: Math.round(Number(formPassingScore)) || 70,
      videoType: formVideoType,
      videoURL: formVideoURL,
      thumbnailURL: formThumbnailURL,
      questions: formQuestions,
    };

    try {
      const url = editingVideoid
        ? `${API_ENDPOINTS.BASEURL}/api/learning-modules/custom/${editingVideoid}`
        : `${API_ENDPOINTS.BASEURL}/api/learning-modules/custom`;
      const method = editingVideoid ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Save failed');
      }

      setSnackbar({ open: true, message: editingVideoid ? t('clm_updated') : t('clm_created'), severity: 'success' });
      setWizardOpen(false);
      resetWizard();
      fetchModules();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || t('clm_save_failed'), severity: 'error' });
    }
  };

  const handleDelete = async (videoid) => {
    try {
      const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/learning-modules/custom/${videoid}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Delete failed');
      setSnackbar({ open: true, message: t('clm_deleted'), severity: 'success' });
      setDeleteConfirm(null);
      fetchModules();
    } catch {
      setSnackbar({ open: true, message: t('clm_delete_failed'), severity: 'error' });
    }
  };

  // --- Question management helpers ---
  const addQuestion = () => {
    setFormQuestions([...formQuestions, {
      question: '',
      options: [
        { option: '', optionAnswerExplanation: '' },
        { option: '', optionAnswerExplanation: '' },
      ],
      correctAnswer: ''
    }]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...formQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setFormQuestions(updated);
  };

  const removeQuestion = (index) => {
    setFormQuestions(formQuestions.filter((_, i) => i !== index));
  };

  const addOption = (qIndex) => {
    const updated = [...formQuestions];
    updated[qIndex].options = [...(updated[qIndex].options || []), { option: '', optionAnswerExplanation: '' }];
    setFormQuestions(updated);
  };

  const updateOption = (qIndex, oIndex, field, value) => {
    const updated = [...formQuestions];
    updated[qIndex].options = [...updated[qIndex].options];
    updated[qIndex].options[oIndex] = { ...updated[qIndex].options[oIndex], [field]: value };
    setFormQuestions(updated);
  };

  const removeOption = (qIndex, oIndex) => {
    const updated = [...formQuestions];
    updated[qIndex].options = updated[qIndex].options.filter((_, i) => i !== oIndex);
    setFormQuestions(updated);
  };

  // --- Render helpers ---
  const renderModuleCard = (module) => {
    const isCustom = module.source === 'custom';
    return (
      <Card key={module.videoid} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
        {module.thumbnailURL && (
          <CardMedia
            component="img"
            sx={{ width: { xs: '100%', sm: 200 }, height: { xs: 140, sm: 'auto' }, objectFit: 'cover' }}
            image={thumbnailSasUrls[module.videoid] || module.thumbnailURL}
            alt={module.title}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <CardContent sx={{ flex: '1 0 auto' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" component="div">{module.title}</Typography>
              <Chip
                label={isCustom ? t('clm_source_custom') : t('clm_source_shared')}
                size="small"
                color={isCustom ? 'secondary' : 'primary'}
                variant="outlined"
              />
              <Chip label={module.videoType} size="small" variant="outlined" />
              {module.deleted && <Chip label={t('clm_deleted_label')} size="small" color="error" />}
            </Stack>
            {module.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{module.description}</Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {t('clm_questions_count')}: {module.questions?.length || 0} | {t('clm_passing_score')}: {module.passingScore || 70}%
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <Typography variant="body2">{t('is_default')}</Typography>
              <Switch
                size="small"
                checked={!!module.is_default}
                onChange={() => handleToggleDefault(module.videoid, !!module.is_default)}
              />
            </Stack>
          </CardContent>
          {isCustom && (
            <CardActions>
              <Tooltip title={t('edit')}>
                <IconButton onClick={() => openEditWizard(module)} size="small"><EditIcon /></IconButton>
              </Tooltip>
              <Tooltip title={t('delete')}>
                <IconButton onClick={() => setDeleteConfirm(module.videoid)} size="small" color="error"><DeleteIcon /></IconButton>
              </Tooltip>
            </CardActions>
          )}
        </Box>
      </Card>
    );
  };

  const renderBasicInfoStep = () => (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField
        label={t('clm_title')}
        value={formTitle}
        onChange={e => setFormTitle(e.target.value)}
        required
        fullWidth
      />
      <TextField
        label={t('clm_description')}
        value={formDescription}
        onChange={e => setFormDescription(e.target.value)}
        fullWidth
        multiline
        minRows={2}
      />
      <TextField
        label={t('clm_passing_score')}
        type="number"
        value={formPassingScore}
        onChange={e => {
          const val = e.target.value;
          if (val === '') { setFormPassingScore(''); return; }
          const intVal = Math.floor(Number(val));
          if (intVal >= 0 && intVal <= 100) setFormPassingScore(intVal);
        }}
        inputProps={{ min: 0, max: 100, step: 1 }}
        helperText={t('clm_passing_score_hint')}
        FormHelperTextProps={{ sx: { color: 'text.secondary', fontSize: '0.75rem' } }}
        sx={{ maxWidth: 200 }}
      />
      <FormControl sx={{ maxWidth: 300 }}>
        <InputLabel>{t('clm_video_type')}</InputLabel>
        <Select
          value={formVideoType}
          label={t('clm_video_type')}
          onChange={e => setFormVideoType(e.target.value)}
        >
          <MenuItem value="AzureStorage">Azure Storage</MenuItem>
          <MenuItem value="YouTube">YouTube</MenuItem>
        </Select>
      </FormControl>
      {formVideoType === 'YouTube' && (
        <TextField
          label={t('clm_youtube_url')}
          value={formVideoURL}
          onChange={e => setFormVideoURL(e.target.value)}
          fullWidth
          placeholder="https://www.youtube.com/embed/..."
        />
      )}
    </Stack>
  );

  const renderMediaStep = () => (
    <Stack spacing={3} sx={{ mt: 1 }}>
      {formVideoType === 'AzureStorage' && (
        <>
          <Typography variant="subtitle1">{t('clm_upload_video')}</Typography>
          {formVideoURL && !videoFile && (
            <Alert severity="info">
              {t('clm_current_video')}: <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{formVideoURL}</Typography>
            </Alert>
          )}
          <Button
            variant="outlined"
            component="label"
            startIcon={videoFile ? <VideoFileIcon /> : <CloudUploadIcon />}
            disabled={uploading}
          >
            {videoFile ? videoFile.name : t('clm_select_video')}
            <input
              type="file"
              hidden
              accept="video/mp4,video/webm,video/quicktime"
              onChange={e => { if (e.target.files[0]) setVideoFile(e.target.files[0]); }}
            />
          </Button>
          {videoFile && (
            <Button size="small" onClick={() => setVideoFile(null)} disabled={uploading}>{t('clm_clear')}</Button>
          )}
        </>
      )}

      <Divider />

      <Typography variant="subtitle1">{t('clm_upload_thumbnail')}</Typography>
      {formThumbnailURL && !thumbnailFile && (
        <Alert severity="info">
          {t('clm_current_thumbnail')}: <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{formThumbnailURL}</Typography>
        </Alert>
      )}
      <Button
        variant="outlined"
        component="label"
        startIcon={thumbnailFile ? <ImageIcon /> : <CloudUploadIcon />}
        disabled={uploading}
      >
        {thumbnailFile ? thumbnailFile.name : t('clm_select_thumbnail')}
        <input
          type="file"
          hidden
          accept="image/png,image/jpeg,image/jpg,image/webp"
          onChange={e => { if (e.target.files[0]) setThumbnailFile(e.target.files[0]); }}
        />
      </Button>
      {thumbnailFile && (
        <Button size="small" onClick={() => setThumbnailFile(null)} disabled={uploading}>{t('clm_clear')}</Button>
      )}

      {uploading && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography variant="body2">{uploadProgress || t('clm_uploading')}</Typography>
        </Stack>
      )}
    </Stack>
  );

  const renderQuizStep = () => (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{t('clm_quiz_questions')}</Typography>
        <Button startIcon={<AddIcon />} onClick={addQuestion} variant="outlined" size="small">
          {t('clm_add_question')}
        </Button>
      </Stack>

      {formQuestions.length === 0 && (
        <Alert severity="info">{t('clm_no_questions_yet')}</Alert>
      )}

      {formQuestions.map((q, qi) => (
        <Card key={qi} variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('clm_question')} #{qi + 1}</Typography>
            <IconButton size="small" color="error" onClick={() => removeQuestion(qi)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <TextField
            label={t('clm_question_text')}
            value={q.question}
            onChange={e => updateQuestion(qi, 'question', e.target.value)}
            fullWidth
            multiline
            sx={{ mb: 2 }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {t('clm_options_hint')}
          </Typography>

          {(q.options || []).map((opt, oi) => (
            <Stack key={oi} direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }} alignItems={{ sm: 'center' }}>
              <Radio
                checked={q.correctAnswer === opt.option}
                onChange={() => updateQuestion(qi, 'correctAnswer', opt.option)}
                size="small"
              />
              <TextField
                label={`${t('clm_option')} ${oi + 1}`}
                value={opt.option}
                onChange={e => updateOption(qi, oi, 'option', e.target.value)}
                size="small"
                sx={{ flex: 2 }}
              />
              <TextField
                label={t('clm_explanation')}
                value={opt.optionAnswerExplanation}
                onChange={e => updateOption(qi, oi, 'optionAnswerExplanation', e.target.value)}
                size="small"
                sx={{ flex: 3 }}
              />
              <IconButton size="small" color="error" onClick={() => removeOption(qi, oi)} disabled={(q.options || []).length <= 2}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}

          <Button size="small" startIcon={<AddIcon />} onClick={() => addOption(qi)} sx={{ mt: 1 }}>
            {t('clm_add_option')}
          </Button>
        </Card>
      ))}
    </Stack>
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1">{t('clm_description_text')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateWizard} size="small">
          {t('clm_create_module')}
        </Button>
      </Stack>

      {loading && <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', my: 2 }} />}

      {!loading && modules.length === 0 && (
        <Alert severity="info">{t('clm_no_custom_modules')}</Alert>
      )}

      {!loading && (() => {
        const filterAndSort = (source, searchQuery, sortBy) => {
          let filtered = modules.filter(m =>
            source === 'custom' ? m.source === 'custom' : m.source !== 'custom'
          );
          if (searchQuery) {
            const q = searchQuery.toLowerCase();
            filtered = filtered.filter(m => (m.title || '').toLowerCase().includes(q));
          }
          switch (sortBy) {
            case 'title-asc':
              return filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
            case 'title-desc':
              return filtered.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
            case 'default-first':
            default:
              return filtered.sort((a, b) => {
                if (!!a.is_default !== !!b.is_default) return a.is_default ? -1 : 1;
                return (a.title || '').localeCompare(b.title || '');
              });
          }
        };

        const builtIn = filterAndSort('shared', searchBuiltIn, sortBuiltIn);
        const custom = filterAndSort('custom', searchCustom, sortCustom);

        const renderSearchSortBar = (searchValue, setSearch, sortValue, setSort) => (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              size="small"
              variant="outlined"
              placeholder={t('search_modules')}
              value={searchValue}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterListIcon fontSize="small" sx={{ color: '#62B6CB' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: '100%', sm: 200 }, flex: 1, maxWidth: 350 }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
              <InputLabel>{t('sort_by')}</InputLabel>
              <Select
                value={sortValue}
                onChange={(e) => setSort(e.target.value)}
                label={t('sort_by')}
              >
                <MenuItem value="default-first">{t('sort_default_first')}</MenuItem>
                <MenuItem value="title-asc">{t('sort_title_az')}</MenuItem>
                <MenuItem value="title-desc">{t('sort_title_za')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        );

        return (
          <>
            <Accordion
              expanded={expandedSection === 'built-in'}
              onChange={(_, isOpen) => setExpandedSection(isOpen ? 'built-in' : false)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('shared_modules')} ({builtIn.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {renderSearchSortBar(searchBuiltIn, setSearchBuiltIn, sortBuiltIn, setSortBuiltIn)}
                {builtIn.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {searchBuiltIn ? (t('no_modules_found') || 'No modules match your search.') : (t('no_modules_available') || 'No modules available.')}
                  </Typography>
                )}
                {builtIn.map(renderModuleCard)}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSection === 'custom'}
              onChange={(_, isOpen) => setExpandedSection(isOpen ? 'custom' : false)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('custom_modules')} ({custom.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {renderSearchSortBar(searchCustom, setSearchCustom, sortCustom, setSortCustom)}
                {custom.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {searchCustom ? (t('no_modules_found') || 'No modules match your search.') : t('clm_no_custom_modules')}
                  </Typography>
                )}
                {custom.map(renderModuleCard)}
              </AccordionDetails>
            </Accordion>
          </>
        );
      })()}

      {/* --- Wizard Dialog --- */}
      <Dialog
        open={wizardOpen}
        onClose={() => { if (!uploading) { setWizardOpen(false); resetWizard(); } }}
        maxWidth="md"
        fullWidth
        fullScreen={false}
      >
        <DialogTitle>
          {editingVideoid ? t('clm_edit_module') : t('clm_create_module')}
        </DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={wizardStep} sx={{ mb: 3 }}>
            <Step><StepLabel>{t('clm_step_basic')}</StepLabel></Step>
            <Step><StepLabel>{t('clm_step_media')}</StepLabel></Step>
            <Step><StepLabel>{t('clm_step_quiz')}</StepLabel></Step>
          </Stepper>

          {wizardStep === WIZARD_STEPS_BASIC && renderBasicInfoStep()}
          {wizardStep === WIZARD_STEPS_MEDIA && renderMediaStep()}
          {wizardStep === WIZARD_STEPS_QUIZ && renderQuizStep()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setWizardOpen(false); resetWizard(); }} disabled={uploading}>
            {t('cancel')}
          </Button>
          {wizardStep > 0 && (
            <Button onClick={handleWizardBack} disabled={uploading}>{t('clm_back')}</Button>
          )}
          {wizardStep < WIZARD_STEPS_QUIZ ? (
            <Button onClick={handleWizardNext} variant="contained" disabled={uploading}>
              {wizardStep === WIZARD_STEPS_MEDIA && (videoFile || thumbnailFile) ? t('clm_upload_next') : t('clm_next')}
            </Button>
          ) : (
            <Button onClick={handleWizardSave} variant="contained" disabled={uploading}>
              {editingVideoid ? t('clm_save_changes') : t('clm_create_module')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* --- Delete Confirmation Dialog --- */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>{t('clm_confirm_delete_title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('clm_confirm_delete_text')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>{t('cancel')}</Button>
          <Button onClick={() => handleDelete(deleteConfirm)} color="error" variant="contained">
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <FixedSnackbar {...snackbar} onClose={() => setSnackbar(s => ({ ...s, open: false }))} />
    </Box>
  );
}

export default CustomLearningModulesManager;
